import { taskCommentService } from "@/services";
import type { TaskComment } from "@flowstate/core";
import { Todo } from "@/types/todo";
import { logger } from "@/lib/logger";

/**
 * Enhanced copy result with detailed feedback information
 * Following industry best practices for user feedback
 */
export interface CopyResult {
  success: boolean;
  taskTitle: string;
  contentType: "basic" | "with_description" | "with_comments" | "complete";
  itemsCopied: {
    title: boolean;
    description: boolean;
    comments: number;
    metadata: boolean;
  };
  formattedText: string;
  error?: string;
}

/**
 * Enhanced task copy utility with comprehensive feedback
 * Copies a task's content to clipboard in human-friendly Markdown format
 * Returns detailed information for enhanced UI feedback
 *
 * Following UX principles:
 * - Visibility of system status (Nielsen's Heuristic #1)
 * - Clear, immediate feedback
 * - Detailed success/error information
 */
export async function copyTaskToClipboard(todo: Todo): Promise<CopyResult> {
  const result: CopyResult = {
    success: false,
    taskTitle: todo.title,
    contentType: "basic",
    itemsCopied: {
      title: false,
      description: false,
      comments: 0,
      metadata: false,
    },
    formattedText: "",
  };

  try {
    // Build formatted text with comprehensive content
    let text = `# Task: ${todo.title}\n\n`;
    result.itemsCopied.title = true;

    // Add task metadata
    const metadata: string[] = [];
    if (todo.priority) {metadata.push(`**Priority:** ${todo.priority}`);}
    if (todo.status) {metadata.push(`**Status:** ${todo.status}`);}
    if (todo.due_date)
      {metadata.push(
        `**Due Date:** ${new Date(todo.due_date).toLocaleDateString()}`
      );}

    if (metadata.length > 0) {
      text += `${metadata.join(" • ")}\n\n`;
      result.itemsCopied.metadata = true;
    }

    // Add description if available
    if (todo.description?.trim()) {
      text += `## Description\n${todo.description}\n\n`;
      result.itemsCopied.description = true;
      result.contentType = "with_description";
    }

    // Fetch and include comments (best-effort)
    try {
      const commentResponse = await taskCommentService.getTaskComments(todo.id);
      if (commentResponse.error) {
        throw commentResponse.error;
      }

      const comments: TaskComment[] = commentResponse.data ?? [];

      if (comments.length > 0) {
        text += `## Comments (${comments.length})\n`;
        comments.forEach((comment, index) => {
          const author =
            comment.user?.full_name || comment.user?.username || "Unknown User";
          const timestamp = comment.created_at
            ? new Date(comment.created_at).toLocaleDateString()
            : "";
          text += `${index + 1}. **${author}** ${timestamp ? `(${timestamp})` : ""}\n   ${comment.content}\n\n`;
        });
        result.itemsCopied.comments = comments.length;
        result.contentType = result.itemsCopied.description
          ? "complete"
          : "with_comments";
      }
    } catch (commentError) {
      // Continue without comments - this is non-blocking
      logger.warn("Could not fetch comments for copy operation", { error: commentError });
    }

    // Add footer with copy timestamp
    text += `---\n*Copied from Task Management System on ${new Date().toLocaleString()}*`;

    // Attempt clipboard write
    await navigator.clipboard.writeText(text);

    result.success = true;
    result.formattedText = text;

    return result;
  } catch (error) {
    // Handle clipboard errors gracefully
    const errorMessage =
      error instanceof Error ? error.message : "Unknown clipboard error";

    result.error = errorMessage;
    result.success = false;

    // Check for common clipboard issues
    if (
      errorMessage.includes("not allowed") ||
      errorMessage.includes("permission")
    ) {
      result.error =
        "Clipboard access denied. Please grant permission or try again.";
    } else if (errorMessage.includes("not supported")) {
      result.error =
        "Clipboard not supported in this browser. Please copy manually.";
    } else if (!navigator.clipboard) {
      result.error =
        "Clipboard API not available. Please use a modern browser.";
    }

    return result;
  }
}

/**
 * Utility function to generate user-friendly success messages
 * Based on what content was actually copied
 */
export function generateCopySuccessMessage(result: CopyResult): string {
  const { taskTitle, contentType, itemsCopied } = result;
  const truncatedTitle =
    taskTitle.length > 30 ? `${taskTitle.substring(0, 30)}...` : taskTitle;

  switch (contentType) {
    case "complete":
      return `Task "${truncatedTitle}" copied with description and ${itemsCopied.comments} comments`;
    case "with_comments":
      return `Task "${truncatedTitle}" copied with ${itemsCopied.comments} comments`;
    case "with_description":
      return `Task "${truncatedTitle}" copied with description`;
    case "basic":
    default:
      return `Task "${truncatedTitle}" copied to clipboard`;
  }
}

/**
 * Utility function to generate detailed success descriptions
 * Provides specific information about what was included
 */
export function generateCopySuccessDescription(result: CopyResult): string {
  const details: string[] = [];

  if (result.itemsCopied.title) {details.push("title");}
  if (result.itemsCopied.description) {details.push("description");}
  if (result.itemsCopied.metadata) {details.push("metadata");}
  if (result.itemsCopied.comments > 0)
    {details.push(`${result.itemsCopied.comments} comments`);}

  return `Included: ${details.join(", ")}`;
}
